import { useMemo, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useNotifications } from '../../../components/notifications/NotificationSystem';
import GoldenRatioContainer from '../../../components/shared/GoldenRatioContainer';
import ExpandableCard from '../../../components/shared/ExpandableCard';
import StandardPage from '../../../components/loading/StandardPage';
import DisciplineRulesSkeleton from '../../../components/skeletons/DisciplineRulesSkeleton';
import ThresholdRuleForm from '../../../components/discipline/disciplineRules/ThresholdRuleForm';
import MentionRuleForm from '../../../components/discipline/disciplineRules/MentionRuleForm';
import ZeroToleranceRuleForm from '../../../components/discipline/disciplineRules/ZeroToleranceRuleForm';
import BehaviorContractForm from '../../../components/discipline/disciplineRules/BehaviorContractForm';
import AccountabilityRuleForm from './AccountabilityRuleForm';
import EscalationRuleForm from '../../../components/discipline/disciplineRules/EscalationRuleForm';
import ScoreAggregationRuleForm from '../../../components/discipline/disciplineRules/ScoreAggregationRuleForm';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  PlusIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  NoSymbolIcon,
  ChartBarIcon,
  ClipboardDocumentIcon,
  CheckCircleIcon,
  ScaleIcon,
  SparklesIcon,
  StarIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import { CSS_CLASSES } from '../../../utils/constants';
import { useDisciplineRules } from '../../../hooks/useDisciplineRules';
import { useDisciplineRulesUIStore } from '../../../stores/disciplineRulesUIStore';
import { useSchoolData } from '../../../hooks/useSchoolData';
import { useSchool } from '../../../contexts/SchoolContext';
import {
  createDisciplineRulesManager,
  RULE_CATEGORIES,
  disciplineRulesUtils
} from '../../../helpers/disciplineRulesManager';
import { disciplineService } from '../../../services/disciplineService';
import { DomainProvider } from '../../domains/components/DomainProvider';
import DomainTags from '../../domains/components/DomainTags';
import { useDomainTypes } from '../../domains/hooks/useDomainTypes';
import { useMode } from '../../../contexts/ContextModeContext';
import { useDomainStore } from '../../../stores/domainStore';
import RulePhaseApplicabilityEditor from '@/features/phases/components/RulePhaseApplicabilityEditor';
import usePhaseConfigurationView from '@/features/phases/usePhaseConfigurationView';
import { reorderVisibleDefinitions } from '@/features/phases/phaseConfigurationView';
import {
  getDomainEventTypeOptions,
  getDomainRuleFieldOptions,
} from '../../domains/utils/domainHelpers';

// Score-aggregation rules are unique to number-mode domains (Academics): they
// aggregate over numeric scores instead of counting categorical events. Defined
// here rather than in RULE_CATEGORIES so it NEVER leaks into discipline /
// recognition / non-numeric domains — those editors stay byte-identical.
const SCORE_RULE_CATEGORY = {
  key: 'score-aggregation',
  label: 'Score Rules',
  recognitionLabel: 'Score Rules',
  domainLabel: 'Score Rules',
  description: 'Trigger follow-ups when scores aggregate past a line (average, count, or streak below/above)',
  recognitionDescription: 'Trigger follow-ups when scores aggregate past a line (average, count, or streak below/above)',
  domainDescription: 'Trigger follow-ups when scores aggregate past a line (average, count, or streak below/above)'
};

export default function DisciplineRules({
  embedded = false,
  domain: domainProp = null,
  onRequestActionDefinition = null,
} = {}) {
  const { showError } = useNotifications();
  // State for available offences
  const [availableOffences, setAvailableOffences] = useState([]);
  const [highlightedRuleId, setHighlightedRuleId] = useState(null);
  const [repairingRuleId, setRepairingRuleId] = useState(null);
  const location = useLocation();

  // Rules mode is driven by PersistentContextBar (mode pill + active domain),
  // NOT a local toggle. Active domain takes precedence; otherwise mode chooses
  // between discipline and recognition rule sets.
  const { mode } = useMode();
  const { activeDomain: storeActiveDomain } = useDomainStore();
  // `domain` prop (panel-local setup selection) overrides the workspace
  // activeDomain so this editor can target a domain that isn't currently active.
  const activeDomain = domainProp ?? storeActiveDomain;
  const phaseView = usePhaseConfigurationView(activeDomain);
  const isProtectedDomainMode = activeDomain?.protected === true;
  const isDomainMode = !!activeDomain && !isProtectedDomainMode;
  const isRecognitionMode = activeDomain
    ? activeDomain.id === 'recognition' : mode === 'recognition';

  const declaredDomainRuleCategories = isDomainMode
    ? (activeDomain?.capabilities?.rules?.mechanisms
      || activeDomain?.capabilities?.rules?.categories
      || [])
      .map((key) => key === 'response' ? 'mention' : key)
    : null;
  const ruleCategories = isDomainMode
    ? [...RULE_CATEGORIES, SCORE_RULE_CATEGORY]
      .filter((category) => declaredDomainRuleCategories.includes(category.key))
    : RULE_CATEGORIES;

  // Get students from school data hook
  const { students } = useSchoolData();
  const { schoolId } = useSchool();
  const { types: domainTypes } = useDomainTypes(
    schoolId,
    isDomainMode ? activeDomain : null,
  );
  const domainEventTypeOptions = useMemo(() => {
    if (!isDomainMode) return null;
    return getDomainEventTypeOptions(activeDomain, domainTypes);
  }, [activeDomain, domainTypes, isDomainMode]);
  const domainRuleFieldOptions = useMemo(
    () => isDomainMode ? getDomainRuleFieldOptions(activeDomain) : null,
    [activeDomain, isDomainMode],
  );
  const domainActionDefinitionOptions = useMemo(() => (
    isDomainMode
      ? (activeDomain?.actionDefinitions || [])
        .filter((definition) => definition.active !== false && definition.retired !== true)
        .map((definition) => ({
          id: definition.id,
          value: definition.id,
          label: definition.label,
          singular: definition.label,
          plural: definition.plural || definition.label,
        }))
      : null
  ), [activeDomain, isDomainMode]);


  // Use centralized state management hooks
  const {
    rules,
    loading,
    loadError,
    error,
    saveStatus,
    invalidRules = [],
    saveRule,
    deleteRule,
    duplicateRule,
    updateSortOrders,
    retryLoad,
    retryLastSave,
    clearError,
    getRulesByType
  } = useDisciplineRules({ isRecognitionMode, domain: isDomainMode ? activeDomain : null });
  const visibleRules = Object.values(rules).flat().filter(phaseView.matches);
  const stats = { total: visibleRules.length, active: visibleRules.filter(rule => rule.active).length,
    inactive: visibleRules.filter(rule => !rule.active).length };

  // Use Zustand store for UI state (with automatic persistence)
  const toggleSection = useDisciplineRulesUIStore(state => state.toggleSection);
  const collapsedSections = useDisciplineRulesUIStore(state => state.collapsedSections);

  // Load available offences with their categories
  useEffect(() => {
    if (isDomainMode) {
      setAvailableOffences([]);
      return undefined;
    }
    const loadOffences = async () => {
      if (!schoolId) return;

      try {
        const [offences, categories] = await Promise.all([
          disciplineService.getOffences(schoolId),
          disciplineService.getDisciplineCategories(schoolId)
        ]);

        // Enrich offences with category data
        const enrichedOffences = offences.map(offence => {
          const category = categories.find(cat => cat.id === offence.categoryId);
          return {
            ...offence,
            category: category
          };
        });

        setAvailableOffences(enrichedOffences);
      } catch (error) {
        console.error('Error loading offences:', error);
      }
    };

    loadOffences();
  }, [schoolId, isDomainMode]);

  // Handle navigation to specific rule via hash
  useEffect(() => {
    if (location.hash && rules && Object.keys(rules).length > 0) {
      const ruleId = location.hash.substring(1); // Remove the # symbol

      // Find which category contains this rule
      let foundCategory = null;
      for (const [categoryKey, categoryRules] of Object.entries(rules)) {
        if (Array.isArray(categoryRules) && categoryRules.some(rule => rule.id === ruleId)) {
          foundCategory = categoryKey;
          break;
        }
      }

      // Expand the section if collapsed
      if (foundCategory && collapsedSections.includes(foundCategory)) {
        toggleSection(foundCategory);
      }

      // Highlight the rule and scroll to it
      setHighlightedRuleId(ruleId);
      setTimeout(() => {
        const element = document.getElementById(`rule-${ruleId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        // Clear the hash from URL after scrolling to prevent re-triggering on updates
        window.history.replaceState(null, '', window.location.pathname);
      }, 300); // Wait for section to expand

      // Clear highlight after 3 seconds
      setTimeout(() => setHighlightedRuleId(null), 3000);
    }
  }, [location.hash, rules, collapsedSections, toggleSection]);

  // Create the isSectionCollapsed function that uses the reactive state
  const isSectionCollapsed = (sectionKey) => {
    return collapsedSections.includes(sectionKey);
  };

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Create business logic manager with hooks
  const manager = useMemo(() => {
    return createDisciplineRulesManager({
      saveRule,
      deleteRule,
      duplicateRule
    });
  }, [saveRule, deleteRule, duplicateRule]);

  // Handle error dismissal
  const handleErrorDismiss = () => {
    clearError();
  };

  // Handle drag end for reordering rules
  const handleDragEnd = async (event, categoryRules, visibleIds) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = categoryRules.findIndex(rule => rule.id === active.id);
      const newIndex = categoryRules.findIndex(rule => rule.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedRules = reorderVisibleDefinitions(categoryRules,
          visibleIds, active.id, over.id);
        if (reorderedRules === categoryRules) return;

        // Calculate new sort orders
        const updates = reorderedRules.map((rule, index) => ({
          id: rule.id,
          sortOrder: (index + 1) * 1000
        }));

        // Update Firebase with new sort orders (collection resolved by hook)
        try {
          await updateSortOrders(updates);
        } catch (error) {
          console.error('Error updating sort order:', error);
        }
      }
    }
  };

  const handleSaveRule = async (formData, categoryType, existingId = null) => {
    try {
      return await manager.processRuleSave(formData, categoryType, existingId);
    } catch (error) {
      console.error('Error saving rule:', error);
      // Error display handled by the hook
      throw error;
    }
  };

  const handleDeleteRule = async (ruleId, ruleData = {}) => {
    try {
      return await manager.processRuleDelete(ruleId, ruleData);
    } catch (error) {
      console.error('Delete failed:', error);
      showError('Delete Failed', error.message || 'Unable to delete rule');
      throw error;
    }
  };

  const handleRepairRule = (issue) => {
    const categoryKey = issue.ruleType === 'response' ? 'mention' : issue.ruleType;
    if (collapsedSections.includes(categoryKey)) toggleSection(categoryKey);
    setRepairingRuleId(issue.ruleId);
    setHighlightedRuleId(issue.ruleId);
    window.setTimeout(() => {
      document.getElementById(`rule-${issue.ruleId}`)?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'center',
      });
    }, 100);
  };

  const handleDeactivateInvalidRule = async (issue) => {
    try {
      await saveRule({ active: false }, issue.ruleId);
    } catch {
      // The shared save banner owns the failure and retry state.
    }
  };


  // Helper function to get the appropriate form component for each category
  const getRuleFormComponent = (categoryKey) => {
    switch (categoryKey) {
      case 'mention':
        return MentionRuleForm;
      case 'accountability':
        return AccountabilityRuleForm;
      case 'escalation':
        return EscalationRuleForm;
      case 'zero-tolerance':
        return ZeroToleranceRuleForm;
      case 'contract':
        return BehaviorContractForm;
      case 'score-aggregation':
        return ScoreAggregationRuleForm;
      case 'threshold':
      default:
        return ThresholdRuleForm;
    }
  };

  // Stats are now provided by the hook

  const Wrapper = embedded ? 'div' : GoldenRatioContainer;
  const body = (
    <Wrapper>
      {loadError && (
        <div className="alert alert-error items-start sm:items-center" role="alert">
          <ExclamationTriangleIcon className="h-5 w-5 flex-none" />
          <div className="min-w-0 flex-1">
            <div className="font-semibold">Rules could not be loaded</div>
            <div className="text-sm break-words">
              {loadError.message || String(loadError)}
            </div>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline"
            onClick={retryLoad}
          >
            Retry loading rules
          </button>
        </div>
      )}

      {!embedded && (
        <div className={`${CSS_CLASSES.CARD_ELEVATED} mb-6`}>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex-1">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-2">
                  <h1 className="text-2xl sm:text-3xl font-bold text-base-content">
                    {isDomainMode
                      ? `${activeDomain.label} Rules`
                      : isRecognitionMode
                        ? 'Recognition Rules'
                        : 'Discipline Rules'}
                  </h1>
                </div>
                <p className="text-base-content/70">
                  {isDomainMode
                    ? `Configure automated rules for ${activeDomain.terminology?.event?.plural || 'events'}`
                    : isRecognitionMode
                      ? 'Configure automated rules for student recognitions'
                      : 'Configure automated rules for student discipline'}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="badge badge-sm badge-neutral whitespace-nowrap">{stats.total} Total</span>
              <span className="badge badge-sm badge-success whitespace-nowrap">{stats.active} Active</span>
              <span className="badge badge-sm badge-warning whitespace-nowrap">{stats.inactive} Inactive</span>
            </div>
            <DomainTags className="mt-2" surface="rules" />
          </div>

          {error && (
            <div className="alert alert-error mt-4">
              <ExclamationTriangleIcon className="h-6 w-6" />
              <div>
                <div className="font-bold">Error</div>
                <div className="text-sm">{error}</div>
              </div>
              <button
                className="btn btn-sm btn-outline"
                onClick={retryLastSave}
              >
                Retry
              </button>
              <button
                className={`${CSS_CLASSES.BUTTON_BASE_WITH_SHADOW} btn-circle btn-ghost`}
                onClick={handleErrorDismiss}
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {embedded && ['saving', 'saved', 'error'].includes(saveStatus) && (
        <div className="flex justify-end">
          {saveStatus === 'saving' && <span className="badge badge-xs badge-info badge-outline">Saving…</span>}
          {saveStatus === 'saved' && <span className="badge badge-xs badge-success badge-outline">Saved</span>}
          {saveStatus === 'error' && <span className="badge badge-xs badge-error badge-outline">Save failed</span>}
        </div>
      )}

      {embedded && error && (
        <div className="alert alert-error items-start sm:items-center">
          <ExclamationTriangleIcon className="h-5 w-5 flex-none" />
          <div className="min-w-0 flex-1">
            <div className="font-semibold">Rule was not saved</div>
            <div className="text-sm break-words">{error}</div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button className="btn btn-sm btn-outline" onClick={retryLastSave}>Retry</button>
            <button className="btn btn-sm btn-ghost" onClick={handleErrorDismiss}>Dismiss</button>
          </div>
        </div>
      )}

      {isDomainMode && invalidRules.length > 0 && (
        <section
          className="rounded-2xl border border-error/40 bg-error/5 p-4"
          aria-label="Rules needing attention"
          role="alert"
        >
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-none text-error" />
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-base-content">
                {invalidRules.length === 1
                  ? '1 active rule needs attention'
                  : `${invalidRules.length} active rules need attention`}
              </h3>
              <p className="mt-1 text-sm text-base-content/70">
                These rules cannot run because their saved configuration no longer matches this Domain.
                Repair or deactivate each rule before relying on it.
              </p>
              <div className="mt-3 space-y-3">
                {invalidRules.map((issue) => {
                  const categoryKey = issue.ruleType === 'response' ? 'mention' : issue.ruleType;
                  const canRepairHere = ruleCategories.some((category) => category.key === categoryKey);
                  const message = issue.message.startsWith(`${issue.title}: `)
                    ? issue.message.slice(issue.title.length + 2)
                    : issue.message;
                  return (
                    <div
                      key={issue.ruleId || `${issue.title}-${issue.ruleType}`}
                      className="flex flex-col gap-3 rounded-xl border border-error/20 bg-base-100 p-3 sm:flex-row sm:items-center"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-base-content">{issue.title}</div>
                        <div className="mt-0.5 text-sm text-error">{message}</div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {canRepairHere && issue.ruleId && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline"
                            onClick={() => handleRepairRule(issue)}
                          >
                            Repair rule
                          </button>
                        )}
                        {issue.ruleId && (
                          <button
                            type="button"
                            className="btn btn-sm btn-ghost text-error"
                            disabled={saveStatus === 'saving'}
                            onClick={() => handleDeactivateInvalidRule(issue)}
                          >
                            Deactivate
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

        {isDomainMode && ruleCategories.length === 0 && (
          <div className="rounded-2xl border border-base-300 bg-base-100 p-6 text-sm text-base-content/70">
            Automated rules are not enabled for this Domain.
          </div>
        )}

        {/* A governed Domain renders only the rule mechanisms it declares. */}
        <div className="space-y-4">
          {ruleCategories.map(category => {
          const savedRules = getRulesByType(category.key);
          const visibleRules = savedRules.filter(rule => phaseView.matches(rule)
            || rule.id === highlightedRuleId || rule.id === repairingRuleId);
          const visibleIds = new Set(visibleRules.map(rule => rule.id));
          const isCollapsed = isSectionCollapsed(category.key);
          const categoryStats = { total: visibleRules.length, active: visibleRules.filter(rule => rule.active).length };
          const categoryInvalidCount = invalidRules.filter((issue) => (
            (issue.ruleType === 'response' ? 'mention' : issue.ruleType) === category.key
          )).length;

          // Only show real rules from database
          const rules = savedRules;

          // Domain mode uses the neutral domain labels; recognition uses its own;
          // discipline (neither) keeps category.label/description byte-identical.
          const categoryLabel = isDomainMode
            ? (category.domainLabel || category.label)
            : isRecognitionMode ? (category.recognitionLabel || category.label) : category.label;
          const categoryDescription = isDomainMode
            ? (category.domainDescription || category.description)
            : isRecognitionMode ? (category.recognitionDescription || category.description) : category.description;

          
          return (
            <ExpandableCard
              key={category.key}
              expanded={!isCollapsed}
              onToggle={() => toggleSection(category.key)}
              flat={embedded}
              header={
                <div className="flex items-center justify-between w-full">
                  <div className={`flex items-center min-w-0 ${embedded ? 'gap-2' : 'gap-3'}`}>
                    {category.key === 'mention' ? (
                      isRecognitionMode
                        ? <SparklesIcon className={`${embedded ? 'w-5 h-5' : 'w-10 h-10'} text-base-content flex-none`} />
                        : <ExclamationTriangleIcon className={`${embedded ? 'w-5 h-5' : 'w-10 h-10'} text-base-content flex-none`} />
                    ) : category.key === 'threshold' ? (
                      <ScaleIcon className={`${embedded ? 'w-5 h-5' : 'w-10 h-10'} text-base-content flex-none`} />
                    ) : category.key === 'zero-tolerance' ? (
                      isRecognitionMode
                        ? <StarIcon className={`${embedded ? 'w-5 h-5' : 'w-10 h-10'} text-base-content flex-none`} />
                        : <NoSymbolIcon className={`${embedded ? 'w-5 h-5' : 'w-10 h-10'} text-base-content flex-none`} />
                    ) : category.key === 'contract' ? (
                      isRecognitionMode
                        ? <ArrowTrendingUpIcon className={`${embedded ? 'w-5 h-5' : 'w-10 h-10'} text-base-content flex-none`} />
                        : <ClipboardDocumentIcon className={`${embedded ? 'w-5 h-5' : 'w-10 h-10'} text-base-content flex-none`} />
                    ) : category.key === 'accountability' ? (
                      <CheckCircleIcon className={`${embedded ? 'w-5 h-5' : 'w-10 h-10'} text-base-content flex-none`} />
                    ) : (
                      <ChartBarIcon className={`${embedded ? 'w-5 h-5' : 'w-10 h-10'} text-base-content flex-none`} />
                    )}
                    <div className="min-w-0">
                      <h3 className={`font-semibold ${embedded ? 'text-sm truncate' : 'text-lg'}`}>{categoryLabel}</h3>
                      {!embedded && <p className="text-sm text-base-content/70">{categoryDescription}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {categoryInvalidCount > 0 ? (
                      <span className={`badge ${embedded ? 'badge-xs' : 'badge-sm'} badge-error whitespace-nowrap`}>
                        {categoryInvalidCount} {categoryInvalidCount === 1 ? 'needs repair' : 'need repair'}
                      </span>
                    ) : categoryStats.active > 0 ? (
                      <span className={`badge ${embedded ? 'badge-xs' : 'badge-sm'} badge-success whitespace-nowrap`}>
                        {categoryStats.active} active
                      </span>
                    ) : (
                      <span className={`badge ${embedded ? 'badge-xs' : 'badge-sm'} badge-ghost whitespace-nowrap`}>
                        {visibleRules.length} {visibleRules.length === 1 ? 'rule' : 'rules'}
                      </span>
                    )}
                  </div>
                </div>
              }
            >
              <div className={embedded ? 'p-3 space-y-3' : 'p-6 space-y-6'}>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(event) => handleDragEnd(event, rules, [...visibleIds])}
                >
                  <SortableContext
                    items={visibleRules.map(r => r.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {/* Render real rules only */}
                    {rules.length > 0 && (
                      rules.map(rule => {
                        const RuleFormComponent = getRuleFormComponent(category.key);
                        const props = {
                          rule: rule,
                          ruleId: rule.id,
                          categoryKey: category.key,
                          onSave: handleSaveRule,
                          onDelete: handleDeleteRule,
                          isRecognitionMode
                        };
                        if (isDomainMode && ['mention', 'threshold', 'escalation'].includes(category.key)) {
                          props.domainEventTypeOptions = domainEventTypeOptions;
                          props.domainActionDefinitionOptions = domainActionDefinitionOptions;
                          props.domainRuleFieldOptions = domainRuleFieldOptions;
                          props.domainLabel = activeDomain.label;
                          props.repairMode = repairingRuleId === rule.id;
                        }


                        // Add availableOffences for rules that need them
                        if (category.key === 'zero-tolerance' || category.key === 'accountability') {
                          props.availableOffences = availableOffences;
                        }

                        // Add both offenses and students for contract rules
                        if (category.key === 'contract') {
                          props.availableOffenses = availableOffences;
                          props.availableStudents = students;
                        }

                        return (
                          <div
                            key={rule.id}
                            hidden={!visibleIds.has(rule.id)}
                            id={`rule-${rule.id}`}
                            className={`transition-all duration-500 ${
                              invalidRules.some((issue) => issue.ruleId === rule.id)
                                ? 'ring-2 ring-error/60 ring-offset-2 rounded-lg '
                                : ''
                            }${
                              highlightedRuleId === rule.id
                                ? 'ring-2 ring-primary ring-offset-2 rounded-lg'
                                : ''
                            }`}
                          >
                            {visibleIds.has(rule.id) && !phaseView.matches(rule) &&
                              <p className="mb-2 text-sm text-base-content/65">This rule is outside the selected phase view.</p>}
                            <RuleFormComponent {...props} />
                            {(isProtectedDomainMode || isDomainMode && (activeDomain.id === 'academics' || /^domain_[a-z0-9_]+_rules$/.test(activeDomain.collections?.rules || activeDomain.rulesCollection || ''))) &&
                              <RulePhaseApplicabilityEditor schoolId={schoolId} domain={activeDomain} rule={rule} onSave={saveRule} />}
                          </div>
                        );
                      })
                    )}
                    {phaseView.ready && visibleRules.length === 0 && (
                      <div className="text-center py-8 text-base-content/60">
                        No {categoryLabel.toLowerCase()} in this view. Use Add {categoryLabel.slice(0, -1)} to create one.
                      </div>
                    )}
                  </SortableContext>
                </DndContext>

                {/* Add Rule Button - Creates real document immediately */}
                <div className="pt-4 border-t border-base-200">
                  <button
                    className={`${CSS_CLASSES.BUTTON_BASE_WITH_SHADOW} btn-outline w-full`}
                    onClick={async () => {
                      // Use utility function to get default data
                      if (isDomainMode && category.key !== 'score-aggregation' && domainActionDefinitionOptions.length === 0) {
                        onRequestActionDefinition?.();
                        return;
                      }
                      let defaultData = disciplineRulesUtils.getDefaultRuleData(category.key);
                      // A draft must be configurable before its action and phase
                      // coverage can pass activation validation.
                      if (isProtectedDomainMode || category.key === 'score-aggregation') defaultData = { ...defaultData, active: false };
                      if (isDomainMode && ['mention', 'threshold', 'escalation'].includes(category.key)) {
                        const firstAction = domainActionDefinitionOptions[0];
                        const nextAction = domainActionDefinitionOptions[1] || firstAction;
                        defaultData = {
                          ...defaultData,
                          title: category.key === 'mention'
                            ? `${activeDomain.label} event response`
                            : category.key === 'threshold'
                              ? `${activeDomain.label} action threshold`
                              : `${activeDomain.label} escalation`,
                          active: false,
                          ...(category.key !== 'escalation' ? {
                            consequenceType: firstAction?.value || '',
                            consequenceTypeId: firstAction?.id || null,
                          } : {}),
                          ...(category.key === 'threshold'
                            ? {
                                countType: firstAction?.value || '',
                                countTypeId: firstAction?.id || null,
                              }
                            : {}),
                          ...(category.key === 'escalation'
                            ? {
                                monitorsType: firstAction?.value || '',
                                monitorsTypeId: firstAction?.id || null,
                                escalatesTo: nextAction?.value || '',
                                escalatesToId: nextAction?.id || null,
                              }
                            : {}),
                        };
                      }
                      await handleSaveRule(defaultData, category.key);
                    }}
                    disabled={loading}
                  >
                    <PlusIcon className="w-4 h-4" />
                    {isDomainMode && category.key !== 'score-aggregation' && domainActionDefinitionOptions.length === 0
                      ? 'Add a Queue follow-up first'
                      : `Add ${categoryLabel.slice(0, -1)}`}
                  </button>
                </div>
              </div>
            </ExpandableCard>
          );
        })}
          </div>
      </Wrapper>
  );

  if (embedded) {
    if (loading) return <DisciplineRulesSkeleton />;
    return <DomainProvider schoolId={schoolId}>{body}</DomainProvider>;
  }

  return (
    <DomainProvider schoolId={schoolId}>
      <StandardPage
        loading={loading}
        error={error}
        skeleton={<DisciplineRulesSkeleton />}
      >
        {body}
      </StandardPage>
    </DomainProvider>
  );
}
